/**
 * ============================================================================
 * KpiCard — Phase 6b frontend (Quality Metrics Dashboard v2)
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: a single metric tile in the 3×4 grid. White surface, 14 px radius,
 *          subtle 1 px border, hover-lift of 2 px (per drafts/42 polish doc).
 *
 * Accessibility:
 *   - The whole card is a <button> so keyboard users get tab focus and Enter
 *     opens the drill-down. We surface the band (green/amber/red) both as a
 *     coloured dot AND a textual label inside `aria-label` so colour is not
 *     the sole signal (WCAG 1.4.1).
 *   - The stale-data badge has its own SR-readable text — never "icon-only".
 * ============================================================================ */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TrendSparkline } from './TrendSparkline';
import styles from './QualityMetricsDashboard.module.css';
import type { QualityMetric, TrafficLightBand } from './useDashboardQuery';

export interface KpiCardProps {
  metric: QualityMetric;
  /** Server-computed wall-clock — used to decide if the value is stale. */
  generatedAtUtc: string;
  onOpen: (metric: QualityMetric) => void;
}

const BAND_DOT_CLASS: Record<TrafficLightBand, string> = {
  GREEN: styles.bandDotGreen,
  AMBER: styles.bandDotAmber,
  RED: styles.bandDotRed,
  UNKNOWN: styles.bandDotUnknown,
};

function formatValue(metric: QualityMetric, locale: string): string {
  if (metric.value == null) return '—';
  const nf = new Intl.NumberFormat(locale, {
    maximumFractionDigits: metric.unit_kind === 'PERCENTAGE' ? 1 : 0,
  });
  switch (metric.unit_kind) {
    case 'PERCENTAGE':
      return `${nf.format(metric.value)}%`;
    case 'DAYS':
      return `${nf.format(metric.value)} d`;
    default:
      return nf.format(metric.value);
  }
}

export function KpiCard({ metric, generatedAtUtc, onOpen }: KpiCardProps) {
  const { t, i18n } = useTranslation();

  const isStale = useMemo(() => {
    const computed = new Date(metric.computed_at_utc).getTime();
    if (!Number.isFinite(computed)) return true;
    const ageMs = Date.now() - computed;
    return ageMs > metric.stale_seconds * 1000;
  }, [metric.computed_at_utc, metric.stale_seconds]);

  const bandLabel = t(`qualityMetrics.band.${metric.band}`, {
    defaultValue: metric.band,
  });
  const metricLabel = t(`qualityMetrics.metric.${metric.metric_key}.label`, {
    defaultValue: metric.label,
  });
  const valueStr = formatValue(metric, i18n.language);

  return (
    <button
      type="button"
      className={styles.kpiCard}
      onClick={() => onOpen(metric)}
      aria-label={t('qualityMetrics.card.openAria', {
        defaultValue: 'Open drill-down for {{metric}} — value {{value}}, band {{band}}',
        metric: metricLabel,
        value: valueStr,
        band: bandLabel,
      })}
    >
      <header className={styles.kpiCardHeader}>
        <span className={styles.kpiCardLabel}>{metricLabel}</span>
        <span
          className={`${styles.bandDot} ${BAND_DOT_CLASS[metric.band]}`}
          aria-hidden="true"
        />
      </header>

      <div className={styles.kpiCardValueRow}>
        <span className={styles.kpiCardValue}>{valueStr}</span>
        {isStale && (
          <span
            className={styles.staleBadge}
            aria-label={t('qualityMetrics.card.staleAria', {
              defaultValue:
                'Stale: last computed {{age}} seconds ago, threshold {{stale}} s.',
              age: Math.floor((Date.now() - new Date(metric.computed_at_utc).getTime()) / 1000),
              stale: metric.stale_seconds,
            })}
          >
            {t('qualityMetrics.card.stale', { defaultValue: 'STALE' })}
          </span>
        )}
      </div>

      <div className={styles.kpiCardTrendRow}>
        <TrendSparkline points={metric.trend} />
        <span className={styles.kpiCardBandLabel}>
          {t('qualityMetrics.card.bandLabel', {
            defaultValue: '{{band}}',
            band: bandLabel,
          })}
        </span>
      </div>

      {/* SR-only summary tying the band and the value together so a single
          announcement explains the row. */}
      <span className="sr-only">
        {t('qualityMetrics.card.srSummary', {
          defaultValue:
            '{{metric}}: {{value}}. Status {{band}}. Last computed {{computed}}.',
          metric: metricLabel,
          value: valueStr,
          band: bandLabel,
          computed: new Date(metric.computed_at_utc).toLocaleString(i18n.language),
        })}
        {' '}
        {t('qualityMetrics.card.generatedAt', {
          defaultValue: 'Dashboard generated at {{ts}}.',
          ts: new Date(generatedAtUtc).toLocaleString(i18n.language),
        })}
      </span>
    </button>
  );
}
